/**
 * Iter-158.13.2.20f.42-relational — Character × Scene AI Hair &
 * Makeup Breakdown accordion.
 *
 * Small presentation-only wrapper. When expanded, mounts the
 * EXISTING <DeptBreakdownSection> with the {scene_id, character_id}
 * scope — filtered server-side to entries related to BOTH this
 * Character AND this Scene. Same underlying `hm_breakdown_entries`
 * document across every surface (Bulk Edit Scenes, Scene Detail,
 * Character Detail). Editing/deleting/adding here mutates the same
 * record surfaced everywhere else.
 *
 *   - default collapsed
 *   - independent per (scene, character) pair (local useState)
 *   - opening/collapsing performs NO writes
 *   - reuses DeptBreakdownSection unchanged — no second CRUD impl
 *
 * Props:
 *   apiBase      workspace-context API base (from useApiBase())
 *   projectId    canonical project id
 *   sceneId      canonical scene id
 *   characterId  canonical character id
 *   testidPrefix testid namespace for the toggle / body (e.g.
 *                "scene-char-ai" or "character-scene-ai")
 */
import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import DeptBreakdownSection from "./DeptBreakdownSection";


export default function CharacterSceneAIBreakdown({
    apiBase,
    projectId,
    sceneId,
    characterId,
    testidPrefix = "character-scene-ai",
}) {
    const [open, setOpen] = useState(false);
    const pairSuffix = `${sceneId}-${characterId}`;

    return (
        <div
            data-testid={`${testidPrefix}-wrap-${pairSuffix}`}
            className="mt-2 rounded-md border border-white/5 bg-black/20"
        >
            <button
                type="button"
                data-testid={`${testidPrefix}-toggle-${pairSuffix}`}
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className="tap w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[10px] uppercase tracking-[0.24em] text-primary/80 font-semibold hover:bg-white/[0.03]"
                title={open ? "Collapse AI Breakdown" : "Expand AI Breakdown"}
            >
                <span>AI Hair &amp; Makeup Breakdown</span>
                {open ? (
                    <ChevronDown size={12} className="text-primary/80" />
                ) : (
                    <ChevronRight size={12} className="text-primary/80" />
                )}
            </button>
            {open ? (
                <div
                    data-testid={`${testidPrefix}-body-${pairSuffix}`}
                    className="px-3 pb-3 pt-1"
                >
                    <DeptBreakdownSection
                        apiBase={apiBase}
                        projectId={projectId}
                        scope={{ scene_id: sceneId, character_id: characterId }}
                    />
                </div>
            ) : null}
        </div>
    );
}
